import axios from 'axios';
import { getSubmissionBackendUrl } from '../constants/api-urls';
import { getApiErrorMessage } from '../utils/apiErrorMessage';
import { getAuth0AuthHeaders } from '../utils/auth0Session';

export interface WalletBalance {
  currencyCode: string;
  available: number;
  eligibleEarnings: number;
  pendingEarnings: number;
  reserved: number;
}

export interface WalletTransaction {
  _id: string;
  currencyCode: string;
  amount: number;
  type: 'cr' | 'dr';
  status: 'eligible' | 'pending' | 'available';
  referenceKey: string;
  campaignId: string | null;
  manualRequestId: string | null;
  createdAt: string;
}

export interface WalletTransactionsPage {
  items: WalletTransaction[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface WalletWithdrawalRequest {
  _id: string;
  currencyCode: string;
  amount: number;
  payoutMethod: 'paypal' | 'other';
  destination: string;
  contactEmail: string;
  notes: string;
  status: 'initiated' | 'completed' | 'rejected';
  isActive: boolean;
  createdAt: string;
}

export interface WalletSummary {
  balances: WalletBalance[];
  activeWithdrawals: WalletWithdrawalRequest[];
}

export type WalletPayoutMethod = WalletWithdrawalRequest['payoutMethod'];

const walletUrl = (curatorId: string, path: string) =>
  `${getSubmissionBackendUrl()}/curatorWallet/${encodeURIComponent(curatorId)}/${path}`;

export const fetchWalletSummary = async (curatorId: string): Promise<WalletSummary> => {
  const response = await axios.get(walletUrl(curatorId, 'summary'), { headers: getAuth0AuthHeaders() });
  return {
    balances: response.data.data.balances,
    activeWithdrawals: response.data.data.activeWithdrawals || [],
  };
};

export const fetchWalletBalances = async (curatorId: string): Promise<WalletBalance[]> =>
  (await fetchWalletSummary(curatorId)).balances;

export const fetchWalletTransactions = async (
  curatorId: string,
  page: number
): Promise<WalletTransactionsPage> => {
  const response = await axios.get(walletUrl(curatorId, 'transactions'), {
    params: { page, limit: 20 },
    headers: getAuth0AuthHeaders(),
  });
  return response.data.data;
};

export const requestWalletWithdrawal = async (
  curatorId: string,
  payload: {
    amount: number;
    currencyCode: string;
    payoutMethod: WalletPayoutMethod;
    destination: string;
    contactEmail: string;
    notes?: string;
  }
) => {
  const response = await axios.post(walletUrl(curatorId, 'withdraw'), payload, {
    headers: getAuth0AuthHeaders(),
  });
  return response.data.data;
};

export const cancelWalletWithdrawal = async (curatorId: string, requestId: string): Promise<WalletSummary> => {
  const response = await axios.post(walletUrl(curatorId, `withdrawals/${encodeURIComponent(requestId)}/cancel`), undefined, {
    headers: getAuth0AuthHeaders(),
  });
  return {
    balances: response.data.data.balances,
    activeWithdrawals: response.data.data.activeWithdrawals || [],
  };
};

export const getWalletErrorMessage = (error: unknown, fallback: string): string =>
  getApiErrorMessage(error, fallback);
